import { useQuery, useSuspenseQuery } from '@tanstack/react-query'
import { createFileRoute, redirect } from '@tanstack/react-router'

import { PageHeader } from '@ems/ui/patterns/page-header'
import { Button } from '@ems/ui/components/button'

import { sessionQuery } from '#admin/features/auth/queries.ts'
import { useSignOut } from '#admin/features/auth/sign-out.ts'
import { AttendanceCard } from '#admin/features/dashboard/attendance-card.tsx'
import { dashboardAccess, readsNothing } from '#admin/features/dashboard/dashboard-access.ts'
import { KpiCards } from '#admin/features/dashboard/kpi-cards.tsx'
import { ModulesPanel } from '#admin/features/dashboard/modules-panel.tsx'
import { NeedsAttention } from '#admin/features/dashboard/needs-attention.tsx'
import { QuickActions, visibleQuickActions } from '#admin/features/dashboard/quick-actions.tsx'
import { RecentRegistrations } from '#admin/features/dashboard/recent-registrations.tsx'
import { RegistrationTrendPanel } from '#admin/features/dashboard/registration-trend.tsx'
import { TicketSales } from '#admin/features/dashboard/ticket-sales.tsx'
import { isPlatformStaff } from '#admin/features/platform/api.ts'
import { can } from '#admin/features/auth/session.ts'
import { moduleAccess } from '#admin/features/tenant/api.ts'
import { manifestQuery } from '#admin/features/tenant/queries.ts'
import { DashboardSkeleton } from '#admin/features/tenant/dashboard-skeleton.tsx'

export const Route = createFileRoute('/_authenticated/dashboard/')({
  pendingComponent: DashboardSkeleton,
  staticData: { crumb: 'Dashboard' },
  /**
   * No tenant resolved. For platform staff that means none has been chosen or provisioned yet,
   * and `/platform` is where either happens -- and `/` lands here, so this is also where a
   * staff member on a fresh stage arrives after signing in.
   */
  loader: async ({ context }) => {
    const manifest = await context.queryClient.ensureQueryData(manifestQuery())
    if (manifest !== null) return
    const session = await context.queryClient.ensureQueryData(sessionQuery())
    if (isPlatformStaff(session)) throw redirect({ to: '/platform' })
  },
  component: DashboardPage,
})

/**
 * The edition at a glance: four figures, the ticket sales, the work that waits, and the newest
 * registrations. The modules the edition has bought sit at the foot, closed.
 *
 * `dashboard` is a core module with no schema of its own. It owns no tables, only a view over
 * what the others hold. Each part reads one module, and only when this person can read it
 * (`dashboardAccess`), so the page sends no request that the API refuses.
 */
function DashboardPage() {
  const { data: manifest } = useSuspenseQuery(manifestQuery())
  const { data: session } = useQuery(sessionQuery())
  const signOut = useSignOut()
  if (manifest === null) {
    return (
      <div className="flex flex-col items-start gap-4">
        <PageHeader
          title="No edition"
          description="Your account is not a member of any edition yet. Ask an administrator to add you."
        />
        <Button variant="outline" disabled={signOut.isPending} onClick={() => signOut.mutate()}>
          Sign out
        </Button>
      </div>
    )
  }

  const access = dashboardAccess(manifest, session)
  const currencyCode = manifest.tenant.currencyCode

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title={manifest.tenant.name}
        description={`${manifest.tenant.timezone} · ${currencyCode}`}
        actions={<QuickActions actions={visibleQuickActions(manifest, session)} />}
      />

      {readsNothing(access) ? (
        <p className="text-sm text-muted-foreground">
          Your role has no figures to show here. Use the menu to open your pages.
        </p>
      ) : null}

      <KpiCards access={access} currencyCode={currencyCode} />

      {access.ticket ? (
        <>
          {/* The door's check-in needs `onsite.write`, as the sidebar's check-in desk does. */}
          <AttendanceCard
            canCheckIn={moduleAccess(manifest, 'onsite') !== 'none' && can(session, 'onsite.write')}
          />
          {/*
            Two rows of two parts. `items-start`: each part is as tall as its own content, so a
            short queue does not stretch to the height of the chart.
          */}
          <div className="grid items-start gap-4 lg:grid-cols-3">
            <RegistrationTrendPanel className="lg:col-span-2" />
            <NeedsAttention access={access} currencyCode={currencyCode} />
          </div>
          <div className="grid items-start gap-4 lg:grid-cols-2">
            <TicketSales />
            <RecentRegistrations />
          </div>
        </>
      ) : (
        <NeedsAttention access={access} currencyCode={currencyCode} />
      )}

      <ModulesPanel manifest={manifest} />
    </div>
  )
}
